/**
 * quotes.service.ts
 *
 * Typed API call wrappers for the quotes domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope.
 */

import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Domain types (mirrors server quotes.types.ts) ────────────────────────────

export interface Quote {
  quoteId: number;
  planId: number;
  /** plans.displayID of the quote's plan, for the editor's "Plan # N". Null with no plan. */
  planDisplayId?: number | null;
  patientId: number | null;
  accountId: number;
  discount: number;
  dateAdded: string | null;
  dateChanged: string | null;
  costAnesthesia: number;
  costOr: number;
  nonDiscountableItems: Array<{ amount: number; description: string }>;
  total: number;
  subTotal: number;
  anesthesiologist: string | null;
  activeQuote: boolean;
  facilityId: number | null;
  notes: string | null;
  credit: number;
  deposit: number;
  depositDate: string | null;
  balanceDue: number;
  payment: number;
  locked: boolean;
  /** True when this quote is hidden from the list by the legacy 'XXX' sentinel. */
  hidden: boolean;
  /**
   * Decoded from `anesthesiologist`, which holds the Totals lock state rather
   * than a person's name. A locked side keeps its value through a Recalc.
   */
  anesthesiaLocked: boolean;
  operatingRoomLocked: boolean;
  procedures: QuoteProcedure[];
  items: QuoteItem[];
  discounts: QuoteDiscount[];
  financing: QuoteFinancing[];
  /** Computed: sum of procedure effectivePrices + costAnesthesia + costOr - credit */
  totalCalculated: number;
}

export interface QuoteProcedure {
  planProcedureId: number;
  categoryId: number | null;
  procId: number | null;
  procSubId: number | null;
  categoryName: string | null;
  subName: string | null;
  specificName: string | null;
  laymansTerms: string | null;
  price: number;
  effectivePrice: number;
  expNumHours: number | null;
  anesthesiaFee: number;
  operatingRoomFee: number;
}

export interface QuoteItem {
  quoteItemId: number;
  itemName: string;
  itemType: string;
  description: string;
  unit: string;
  costItemId: number | null;
  categoryId: number | null;
  isInventory: boolean;
  taxable: boolean;
  curRate: number;
  /** A dollar amount on this table, not a rate. */
  salesTax: number;
  quantity: number;
  lineTotal: number;
}

// ─── Request payload types ────────────────────────────────────────────────────

export interface CreateQuotePayload {
  /** One of planId or patientId. A quote started from the patient chart has
   *  no plan yet and gets one later through Find Plan. */
  planId?: number;
  patientId?: number;
  facilityId?: number;
  costAnesthesia?: number;
  costOr?: number;
  discount?: number;
  notes?: string;
  anesthesiologist?: string;
  deposit?: number;
}

export interface UpdateQuotePayload {
  facilityId?: number;
  costAnesthesia?: number;
  costOr?: number;
  discount?: number;
  notes?: string;
  anesthesiologist?: string;
  deposit?: number;
  depositDate?: string;
  credit?: number;
  locked?: boolean;
  activeQuote?: boolean;
}

export interface AddItemPayload {
  itemName: string;
  curRate: number;
  salesTax?: number;
  quantity?: number;
  description?: string;
}

/**
 * One row of the patient chart's Quotes list. Shaped by the server to match the
 * legacy pageID=20 columns, including its two quirks: `balance` is null on any
 * quote that is not the active one, and quotes carrying the legacy 'XXX'
 * sentinel are never returned at all.
 */
export interface PatientQuoteRow {
  quoteId: number;
  planId: number;
  planDisplayId: number | null;
  created: string | null;
  procedures: string[];
  effectiveFee: number;
  anesthesia: number;
  operatingRoom: number;
  total: number;
  balance: number | null;
  activeQuote: boolean;
  locked: boolean;
}

// ─── Service methods ──────────────────────────────────────────────────────────

/**
 * Fetch the quotes shown on a patient's chart.
 */
export function getQuotesByPatient(patientId: number): Promise<PatientQuoteRow[]> {
  return apiClient
    .get<ApiResponse<PatientQuoteRow[]>>(`/patients/${patientId}/quotes`)
    .then((r) => r.data.data);
}

/**
 * Fetch all quotes for a plan.
 */
export function getQuotesByPlan(planId: number): Promise<Quote[]> {
  return apiClient
    .get<ApiResponse<Quote[]>>(`/plans/${planId}/quotes`)
    .then((r) => r.data.data);
}

/**
 * Fetch a single quote with its procedures and items.
 */
export function getQuote(quoteId: number): Promise<Quote> {
  return apiClient
    .get<ApiResponse<Quote>>(`/quotes/${quoteId}`)
    .then((r) => r.data.data);
}

/**
 * Create a new quote for a plan.
 */
export function createQuote(data: CreateQuotePayload): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>('/quotes', data)
    .then((r) => r.data.data);
}

/**
 * Update a quote's header fields (partial update supported).
 */
export function updateQuote(quoteId: number, data: UpdateQuotePayload): Promise<Quote> {
  return apiClient
    .put<ApiResponse<Quote>>(`/quotes/${quoteId}`, data)
    .then((r) => r.data.data);
}

/**
 * Mark this quote active. Other quotes keep their own Active flag, as in
 * legacy (see QuotesController::setActive).
 */
export function setActiveQuote(quoteId: number): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>(`/quotes/${quoteId}/set-active`)
    .then((r) => r.data.data);
}

/**
 * Copy a quote into a new one, as legacy's Clone does: from the editor's
 * working state when given (procedures, products, discounts and financing),
 * otherwise from the saved quote. The copy starts inactive and unlocked.
 */
export function cloneQuote(quoteId: number, state?: QuoteEditorState): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>(`/quotes/${quoteId}/clone`, state ?? {})
    .then((r) => r.data.data);
}

/**
 * Unlock a locked quote. The server allows this only above user level 2, as
 * legacy's Unlock button does.
 */
export function unlockQuote(quoteId: number): Promise<Quote> {
  return apiClient
    .post<ApiResponse<Quote>>(`/quotes/${quoteId}/unlock`)
    .then((r) => r.data.data);
}

/**
 * Hard-delete a quote. Returns void on success.
 */
export function deleteQuote(quoteId: number): Promise<void> {
  return apiClient.delete(`/quotes/${quoteId}`).then(() => undefined);
}

// ─── Procedures ───────────────────────────────────────────────────────────────

// ─── Items ────────────────────────────────────────────────────────────────────

/**
 * Add a miscellaneous item to a quote.
 */
export function addItem(quoteId: number, data: AddItemPayload): Promise<QuoteItem> {
  return apiClient
    .post<ApiResponse<QuoteItem>>(`/quotes/${quoteId}/items`, data)
    .then((r) => r.data.data);
}

/**
 * Remove a miscellaneous item from a quote.
 */
export function removeItem(quoteId: number, quoteItemId: number): Promise<void> {
  return apiClient
    .delete(`/quotes/${quoteId}/items/${quoteItemId}`)
    .then(() => undefined);
}

// ─── Quote editor ─────────────────────────────────────────────────────────────

export interface QuoteDiscount {
  quoteDiscountId: number;
  discountId: number;
  name: string;
  description: string;
  isPercent: boolean;
  /** all | proc | or | anes | mat — legacy's discount scope code. */
  quoteElement: string;
  amount: number;
  active: boolean;
}

export interface QuoteFinancing {
  quoteFinancingId: number;
  paymentMethodId: number;
  methodName: string | null;
  termIds: number[];
}

/** What the Totals panel shows. Computed on the server so the list agrees. */
export interface QuoteTotals {
  procedureTotal: number;
  itemTotal: number;
  totalHours: number;
  anesthesia: number;
  operatingRoom: number;
  procedureDiscount: number;
  otherDiscounts: number;
  credit: number;
  total: number;
  balanceDue: number;
  taxRate: number;
  facilityRateType: number;
}

export interface CatalogProduct {
  costItemId: number;
  name: string;
  type: string;
  description: string;
  defaultRate: number;
  unit: string;
  taxable: boolean;
  isInventory: boolean;
  onHand: number;
  categoryId: number | null;
  categoryName: string | null;
}

export interface CatalogDiscount {
  discountId: number;
  name: string;
  description: string;
  isPercent: boolean;
  discountType: 'percentage' | 'fixed';
  quoteElement: string;
  amount: number;
  active: boolean;
}

export interface CatalogPaymentMethod {
  paymentMethodId: number;
  name: string;
  terms: { termId: number; months: number; apr: number }[];
}

export interface CatalogFacility {
  facilityId: number;
  name: string;
  taxRate: number;
  facilityRateType: number;
  nonAdminAllowEdit: boolean;
  isDefault: boolean;
}

/** The editor's working state, as sent to recalc and save. */
export interface QuoteEditorState {
  facilityId: number | null;
  procedures: Partial<QuoteProcedure>[];
  items: Partial<QuoteItem>[];
  discounts: Partial<QuoteDiscount>[];
  financing: Partial<QuoteFinancing>[];
  anesthesiaLocked: boolean;
  operatingRoomLocked: boolean;
  costAnesthesia: number;
  costOr: number;
  notes?: string;
  activeQuote?: boolean;
  /** The Locked checkbox. Save can lock a quote; only unlockQuote clears it. */
  locked?: boolean;
  /**
   * The plan Find Plan chose, stored by Save. Save refuses a different plan
   * for a quote already on one.
   */
  planId?: number;
}

/** Totals for the working state, without saving it. */
export function recalcQuote(quoteId: number, state: QuoteEditorState): Promise<QuoteTotals> {
  return apiClient
    .post<ApiResponse<QuoteTotals>>(`/quotes/${quoteId}/recalc`, state)
    .then((r) => r.data.data);
}

/** Save the whole quote — header and all four child collections — at once. */
export function saveQuote(quoteId: number, state: QuoteEditorState): Promise<Quote> {
  return apiClient
    .put<ApiResponse<Quote>>(`/quotes/${quoteId}/save`, state)
    .then((r) => r.data.data);
}

export function getProductCatalog(): Promise<CatalogProduct[]> {
  return apiClient
    .get<ApiResponse<CatalogProduct[]>>('/quotes/catalog/products')
    .then((r) => r.data.data);
}

export function getDiscountCatalog(): Promise<CatalogDiscount[]> {
  return apiClient
    .get<ApiResponse<CatalogDiscount[]>>('/quotes/catalog/discounts')
    .then((r) => r.data.data);
}

export function getPaymentMethodCatalog(): Promise<CatalogPaymentMethod[]> {
  return apiClient
    .get<ApiResponse<CatalogPaymentMethod[]>>('/quotes/catalog/payment-methods')
    .then((r) => r.data.data);
}

/**
 * The hour steps this facility prices against, ascending.
 *
 * The quote's summed hours is the exact key for the anaesthesia and OR fee
 * lookup, so Hours has to be chosen from these rather than typed — an off-scale
 * value silently prices at the facility's maxHours row.
 */
export function getQuoteHourBands(facilityId: number): Promise<number[]> {
  return apiClient
    .get<ApiResponse<number[]>>(`/quotes/catalog/facilities/${facilityId}/hour-bands`)
    .then((r) => r.data.data ?? []);
}

/**
 * Facilities a quote can be written against. With a quoteId, that quote's
 * current facility is included even after it is retired or taken off quotes,
 * so the picker still shows it.
 */
export function getQuoteFacilities(quoteId?: number): Promise<CatalogFacility[]> {
  return apiClient
    .get<ApiResponse<CatalogFacility[]>>('/quotes/catalog/facilities', {
      params: quoteId ? { quoteId } : undefined,
    })
    .then((r) => r.data.data);
}

// ─── Printing ─────────────────────────────────────────────────────────────────

export interface QuotePrintTemplate {
  templateId: number;
  name: string;
}

export function getQuotePrintTemplates(): Promise<QuotePrintTemplate[]> {
  return apiClient
    .get<ApiResponse<QuotePrintTemplate[]>>('/quotes/print-templates')
    .then((r) => r.data.data);
}

/**
 * The quote as PDF bytes.
 *
 * Returned as a Blob rather than a link because the viewer sandbox blocks
 * downloads a page starts itself; the caller mints its own object URL per use
 * and revokes it, the same way payment receipts are handled.
 *
 * With `state` — the editor's working copy, the same body recalc takes — the
 * server prints that instead of the saved quote, and saves nothing.
 */
function quotePdf(
  quoteId: number,
  kind: 'preview' | 'download',
  templateId?: number,
  state?: QuoteEditorState,
): Promise<Blob> {
  const url = `/quotes/${quoteId}/print/${kind}`;
  const config = { responseType: 'blob' as const, headers: { Accept: 'application/pdf' } };

  const request = state
    ? apiClient.post<Blob>(url, { ...state, templateId: templateId ?? 0 }, config)
    : apiClient.get<Blob>(url, { ...config, params: templateId ? { templateId } : {} });

  return request.then((r) => r.data);
}

export function previewQuotePdf(quoteId: number, templateId?: number, state?: QuoteEditorState): Promise<Blob> {
  return quotePdf(quoteId, 'preview', templateId, state);
}

export function downloadQuotePdf(quoteId: number, templateId?: number, state?: QuoteEditorState): Promise<Blob> {
  return quotePdf(quoteId, 'download', templateId, state);
}

// ─── Toolbar actions ──────────────────────────────────────────────────────────

export interface QuotePreset {
  presetId: number;
  name: string;
  categoryId: number;
}

export interface QuotePresetCatalog {
  categories: { categoryId: number; name: string }[];
  presets: QuotePreset[];
}

export interface QuotePlanOption {
  planId: number;
  displayId: number;
}

export interface QuotePortalStatus {
  available: boolean;
  published: boolean;
}

export interface QuoteHistoryEntry {
  quoteHistoryId: number;
  savedAt: string | null;
  savedBy: string | null;
  total: number;
  counts: { procedures: number; items: number; discounts: number; financing: number };
}

export function getQuotePresets(): Promise<QuotePresetCatalog> {
  return apiClient
    .get<ApiResponse<QuotePresetCatalog>>('/quotes/presets')
    .then((r) => r.data.data);
}

/**
 * Replaces the quote's procedures, products and discounts with the preset's,
 * with its fees and lock flags. Leaves financing as it is.
 */
export function applyQuotePreset(quoteId: number, presetId: number): Promise<unknown> {
  return apiClient
    .post<ApiResponse<unknown>>(`/quotes/${quoteId}/presets/apply`, { presetId })
    .then((r) => r.data.data);
}

export function saveQuoteAsPreset(quoteId: number, name: string, categoryId: number): Promise<unknown> {
  return apiClient
    .post<ApiResponse<unknown>>(`/quotes/${quoteId}/presets/save`, { name, categoryId })
    .then((r) => r.data.data);
}

export function getQuotePlanOptions(quoteId: number): Promise<QuotePlanOption[]> {
  return apiClient
    .get<ApiResponse<QuotePlanOption[]>>(`/quotes/${quoteId}/plans`)
    .then((r) => r.data.data);
}

/**
 * Attach a quote with no plan to one of its patient's plans. The server
 * refuses to move or detach a quote already on a plan. The editor does not
 * call this: Find Plan's choice is stored by saveQuote.
 */
export function attachQuotePlan(quoteId: number, planId: number): Promise<unknown> {
  return apiClient
    .put<ApiResponse<unknown>>(`/quotes/${quoteId}/plan`, { planId })
    .then((r) => r.data.data);
}

export function getQuotePortalStatus(quoteId: number): Promise<QuotePortalStatus> {
  return apiClient
    .get<ApiResponse<QuotePortalStatus>>(`/quotes/${quoteId}/portal`)
    .then((r) => r.data.data);
}

/**
 * Share a quote in the patient portal.
 *
 * merchantId is required: it lands on portal_patient_quotes.accountAuthnetID,
 * which the portal reads to decide whether the patient can pay the quote.
 */
export function publishQuoteToPortal(quoteId: number, merchantId: number): Promise<QuotePortalStatus> {
  return apiClient
    .post<ApiResponse<QuotePortalStatus>>(`/quotes/${quoteId}/portal`, { merchantId })
    .then((r) => r.data.data);
}

export function unpublishQuoteFromPortal(quoteId: number): Promise<QuotePortalStatus> {
  return apiClient
    .delete<ApiResponse<QuotePortalStatus>>(`/quotes/${quoteId}/portal`)
    .then((r) => r.data.data);
}

export function getQuoteHistory(quoteId: number): Promise<QuoteHistoryEntry[]> {
  return apiClient
    .get<ApiResponse<QuoteHistoryEntry[]>>(`/quotes/${quoteId}/history`)
    .then((r) => r.data.data);
}

export function downloadQuoteExcel(quoteId: number): Promise<Blob> {
  return apiClient
    .get<Blob>(`/quotes/${quoteId}/excel`, { responseType: 'blob' })
    .then((r) => r.data);
}
